import { z } from 'zod';

import { apiClient } from './client';

// Acceptance acts (T10). Tenant can view, respond to check-out, initiate signing.

export const ActTypeZ = z.enum(['check_in', 'check_out']);
export const ActStatusZ = z.enum(['pending', 'submitted', 'signed', 'cancelled']);

export const RoomConditionZ = z.object({
  room: z.string(),
  condition: z.enum(['excellent', 'good', 'fair', 'damaged']).optional(),
  note: z.string().optional(),
  photos: z.array(z.object({ url: z.string() })).default([]),
});

export const MeterReadingZ = z.object({
  type: z.string(),
  value: z.number(),
  photo_url: z.string().nullable().optional(),
});

export const InventoryItemZ = z.object({
  name: z.string(),
  quantity: z.number().int().optional(),
  condition: z.string().optional(),
  photos: z.array(z.object({ url: z.string() })).default([]),
});

export const KeyItemZ = z.object({
  name: z.string(),
  returned: z.boolean().optional(),
});

export const DocumentBriefZ = z.object({
  id: z.number().int(),
  url: z.string().optional(),
  // .catch(undefined): .optional() терпит только отсутствие; ПРИСУТСТВУЮЩЕЕ
  // незнакомое значение всё равно бы уронило парсинг списка актов.
  signing_status: z
    .enum(['pending_tenant', 'pending_owner', 'signed', 'none'])
    .optional()
    .catch(undefined),
});

export const AcceptanceActZ = z.object({
  id: z.number().int(),
  lease_id: z.number().int(),
  type: ActTypeZ,
  tenant_status: ActStatusZ,
  owner_status: ActStatusZ,
  signing_status: z.string().optional(),
  meter_readings: z.array(MeterReadingZ).default([]),
  room_conditions: z.array(RoomConditionZ).default([]),
  inventory_items: z.array(InventoryItemZ).default([]),
  keys: z.array(KeyItemZ).default([]),
  document: DocumentBriefZ.nullable().optional(),
  created_at: z.string(),
});
export type AcceptanceAct = z.infer<typeof AcceptanceActZ>;

const ListZ = z.object({ data: z.array(AcceptanceActZ) });
const OneZ = z.object({ data: AcceptanceActZ });

export async function listActsForLease(leaseId: number): Promise<AcceptanceAct[]> {
  const res = await apiClient.get(`/leases/${leaseId}/acceptance-acts`);
  return ListZ.parse(res.data).data;
}

export async function getAct(id: number): Promise<AcceptanceAct> {
  const res = await apiClient.get(`/acceptance-acts/${id}`);
  return OneZ.parse(res.data).data;
}

export async function tenantResponse(id: number, form: FormData): Promise<AcceptanceAct> {
  const res = await apiClient.patch(`/acceptance-acts/${id}/tenant-response`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return OneZ.parse(res.data).data;
}

// Signing

export const SigningInitiateZ = z.object({
  data: z.object({
    qrCode: z.string(),
    eGovMobileLaunchLink: z.string().optional(),
    operationId: z.string(),
    expireAt: z.string(),
  }),
});

export async function initiateSigning(documentId: number): Promise<{
  qrCode: string;
  eGovMobileLaunchLink?: string;
  operationId: string;
  expireAt: string;
}> {
  const res = await apiClient.post(`/signing/${documentId}/initiate`);
  return SigningInitiateZ.parse(res.data).data;
}

export const SigningStatusZ = z.object({
  status: z.enum(['waiting', 'done', 'canceled', 'fail', 'expired']),
  expire_at: z.string().optional(),
});

export async function signingStatus(documentId: number): Promise<z.infer<typeof SigningStatusZ>> {
  const res = await apiClient.get(`/signing/${documentId}/status`);
  return SigningStatusZ.parse(res.data);
}

export async function signingSendSms(documentId: number): Promise<{ phone_masked: string }> {
  const res = await apiClient.post(`/signing/${documentId}/send-sms`);
  return z.object({ phone_masked: z.string() }).parse(res.data);
}

export const actsApi = {
  listForLease: listActsForLease,
  get: getAct,
  tenantResponse,
  initiateSigning,
  signingStatus,
  signingSendSms,
};
